// Lista desplegable propia del DS (`input-dropdown`, Figma 6617:6299) sobre un
// `<select is="select-custom">`: mejora progresiva, sin JS queda el select nativo.
//
// Sigue el patrón "select-only combobox" de WAI-ARIA (APG): la caja visible
// (`select-selected`) es el `combobox` enfocable y la lista (`select-options`) su
// `listbox`; el foco se queda SIEMPRE en la caja y la opción activa se anuncia con
// `aria-activedescendant`. Teclado: flechas, Inicio/Fin, Re/Av Pág, Intro/Espacio
// para abrir o elegir, Esc para cerrar sin elegir, Tab elige la activa y sigue, y
// escribir letras salta a la opción que empieza por ellas.
//
// El select nativo sigue en el DOM y es la fuente de verdad: el formulario lo
// envía, su `required` sigue validando (por eso no va con display: none, que
// impediría al navegador enfocarlo para avisar) y al elegir se disparan en él
// `input` y `change`, con burbujeo, como en un select de verdad. Si otro código
// le cambia las opciones (p. ej. ciudades según la provincia) la lista se rehace.
let instancias = 0
const TECLAS_QUE_ABREN = ['ArrowDown', 'ArrowUp', 'Enter', ' ', 'Home', 'End']
const SALTO_DE_PAGINA = 10
const PAUSA_BUSQUEDA_AL_TECLEAR = 500

class SelectCustom extends HTMLSelectElement {
  connectedCallback () {
    if (this.newSelect) return

    this.uid = `select-custom-${++instancias}`
    this.newSelect = document.createElement('select-custom')
    this.newSelect.className = this.className
    this.parentNode.insertBefore(this.newSelect, this)

    this.createSelectableElement()
    this.createListbox()
    this.addSearchInputIfNeeded()
    this.importOptionElements()
    this.hideNativeSelect()
    this.bindEvents()
  }

  disconnectedCallback () {
    document.removeEventListener('click', this.onDocumentClick)
    this.observer?.disconnect()
    this.newSelect?.remove()
    this.newSelect = null
  }

  get selectedOptionText () {
    return this.options[this.selectedIndex] && this.options[this.selectedIndex].textContent
  }

  get selectedOptionValue () {
    return this.options[this.selectedIndex] && this.options[this.selectedIndex].value
  }

  get originalOptions () {
    return this.querySelectorAll('option')
  }

  get isOpen () {
    return this.newSelect.classList.contains('select-custom-open')
  }

  get renderedOptions () {
    return Array.from(this.optionsWrapper.querySelectorAll('select-option'))
  }

  // Las que se pueden recorrer con el teclado: ni deshabilitadas ni ocultas por el filtro.
  get navigableOptions () {
    return this.renderedOptions.filter(option =>
      !option.hasAttribute('aria-disabled') && option.style.display !== 'none')
  }

  createSelectableElement () {
    this.seleccionable = document.createElement('select-selected')
    this.seleccionable.id = `${this.uid}-caja`
    this.seleccionable.setAttribute('role', 'combobox')
    this.seleccionable.setAttribute('aria-haspopup', 'listbox')
    this.seleccionable.setAttribute('aria-expanded', 'false')
    this.seleccionable.setAttribute('aria-controls', `${this.uid}-lista`)
    this.copyAccessibleName()
    this.newSelect.appendChild(this.seleccionable)
  }

  // El nombre accesible sale del label del select nativo (o de su aria-label) y la
  // descripción (ayuda o error de la molécula) de su aria-describedby.
  copyAccessibleName () {
    const label = this.labels?.[0]
    if (label) {
      label.id ||= `${this.uid}-label`
      this.seleccionable.setAttribute('aria-labelledby', label.id)
    } else if (this.getAttribute('aria-label')) {
      this.seleccionable.setAttribute('aria-label', this.getAttribute('aria-label'))
    }
    const descripcion = this.getAttribute('aria-describedby')
    if (descripcion) this.seleccionable.setAttribute('aria-describedby', descripcion)
    if (this.required) this.seleccionable.setAttribute('aria-required', 'true')
  }

  createListbox () {
    this.optionsWrapper = document.createElement('select-options')
    this.optionsWrapper.id = `${this.uid}-lista`
    this.optionsWrapper.setAttribute('role', 'listbox')
    const etiqueta = this.seleccionable.getAttribute('aria-labelledby')
    if (etiqueta) this.optionsWrapper.setAttribute('aria-labelledby', etiqueta)
    this.newSelect.appendChild(this.optionsWrapper)
  }

  importOptionElements () {
    this.renderedOptions.forEach(option => option.remove())
    Array.from(this.originalOptions).forEach((option, index) => this.importOption(option, index))
    this.syncFromNative()
  }

  importOption (originalOption, index) {
    if (originalOption.hidden || originalOption.style.display === 'none') return

    const newOption = document.createElement('select-option')
    newOption.id = `${this.uid}-opcion-${index}`
    newOption.setAttribute('role', 'option')
    newOption.textContent = originalOption.textContent
    newOption.dataset.index = index
    newOption.dataset.value = originalOption.value
    if (originalOption.disabled) newOption.setAttribute('aria-disabled', 'true')
    this.optionsWrapper.appendChild(newOption)
  }

  // Oculto pero renderizado: el navegador tiene que poder enfocarlo para mostrar
  // el aviso de `required`. Fuera del orden de tabulación y del árbol accesible,
  // que ya los ocupa la caja.
  hideNativeSelect () {
    this.classList.add('select-custom-native')
    this.tabIndex = -1
    this.setAttribute('aria-hidden', 'true')
  }

  bindEvents () {
    this.seleccionable.addEventListener('click', event => {
      event.stopPropagation()
      this.isOpen ? this.closeSelect() : this.openSelect()
    })
    this.seleccionable.addEventListener('keydown', event => this.onKeydown(event))

    // mousedown sin foco: elegir con el ratón no debe robarle el foco a la caja.
    this.optionsWrapper.addEventListener('mousedown', event => {
      if (event.target.closest('select-option')) event.preventDefault()
    })
    this.optionsWrapper.addEventListener('click', event => {
      event.stopPropagation()
      const option = event.target.closest('select-option')
      if (option) this.chooseOption(option)
    })

    this.newSelect.addEventListener('focusout', event => {
      if (!this.newSelect.contains(event.relatedTarget)) this.closeSelect()
    })

    this.onDocumentClick = event => {
      if (!this.newSelect?.contains(event.target)) this.closeSelect()
    }
    document.addEventListener('click', this.onDocumentClick)

    // Un clic en el label enfoca el nativo (oculto): se lo pasamos a la caja. Salvo
    // cuando lo enfoca el navegador para avisar de `required`, que quitaría el aviso.
    this.addEventListener('invalid', () => { this.enfocadoPorValidacion = true })
    this.addEventListener('focus', () => {
      if (this.enfocadoPorValidacion) {
        this.enfocadoPorValidacion = false
        return
      }
      this.seleccionable.focus()
    })

    this.addEventListener('change', () => this.syncFromNative())
    this.form?.addEventListener('reset', () => setTimeout(() => this.syncFromNative()))

    this.observer = new MutationObserver(() => this.importOptionElements())
    const atributos = ['disabled', 'hidden', 'label', 'style', 'value']
    this.observer.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: atributos })
  }

  addSearchInputIfNeeded () {
    if (this.dataset.searchable !== 'true') return

    this.searchInput = document.createElement('input')
    this.searchInput.type = 'search'
    this.searchInput.className = 'select-custom-search'
    this.searchInput.placeholder = this.dataset.searchPlaceholder || 'Buscar...'
    this.searchInput.setAttribute('aria-label', this.searchInput.placeholder)
    this.searchInput.setAttribute('aria-controls', this.optionsWrapper.id)
    this.searchInput.addEventListener('click', event => event.stopPropagation())
    this.searchInput.addEventListener('input', () => {
      this.filterOptions()
      this.setActiveOption(this.navigableOptions[0])
    })
    this.searchInput.addEventListener('keydown', event => this.onKeydown(event))
    this.optionsWrapper.insertBefore(this.searchInput, this.optionsWrapper.firstChild)
  }

  filterOptions () {
    const needle = this.normalizeForSearch(this.searchInput.value)
    this.renderedOptions.forEach(option => {
      const matches = !needle || this.normalizeForSearch(option.textContent).includes(needle)
      option.style.display = matches ? '' : 'none'
    })
  }

  normalizeForSearch (text) {
    return text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim()
  }

  // Estado visible a partir del nativo: texto de la caja, opción marcada y
  // deshabilitado. Se llama tras cualquier cambio que no hayamos hecho nosotros.
  syncFromNative () {
    if (!this.newSelect) return

    this.seleccionable.textContent = this.selectedOptionText || ''
    this.newSelect.classList.toggle('select-custom-empty', !this.selectedOptionValue)
    this.renderedOptions.forEach(option => {
      const elegida = Number(option.dataset.index) === this.selectedIndex
      option.classList.toggle('selected-option', elegida)
      option.setAttribute('aria-selected', String(elegida))
    })
    this.newSelect.classList.toggle('select-custom-disabled', this.disabled)
    this.seleccionable.tabIndex = this.disabled ? -1 : 0
    if (this.disabled) {
      this.seleccionable.setAttribute('aria-disabled', 'true')
    } else {
      this.seleccionable.removeAttribute('aria-disabled')
    }
  }

  openSelect () {
    if (this.disabled || this.isOpen) return

    this.newSelect.classList.add('select-custom-open')
    this.seleccionable.setAttribute('aria-expanded', 'true')
    const elegida = this.renderedOptions.find(option => Number(option.dataset.index) === this.selectedIndex)
    this.setActiveOption(elegida && !elegida.hasAttribute('aria-disabled') ? elegida : this.navigableOptions[0])
    if (this.searchInput) this.searchInput.focus()
  }

  closeSelect () {
    if (!this.newSelect || !this.isOpen) return

    this.newSelect.classList.remove('select-custom-open')
    this.seleccionable.setAttribute('aria-expanded', 'false')
    this.setActiveOption(null)
    this.resetSearch()
    if (this.newSelect.contains(document.activeElement)) this.seleccionable.focus()
  }

  // Al compactar el desplegable dejamos el buscador vacío y todas las opciones
  // visibles, para que la siguiente apertura empiece desde cero y no se quede
  // con el filtro anterior aplicado. No-op si este select no es buscable.
  resetSearch () {
    if (!this.searchInput || this.searchInput.value === '') return
    this.searchInput.value = ''
    this.filterOptions()
  }

  setActiveOption (option) {
    this.renderedOptions.forEach(each => each.classList.toggle('select-option-active', each === option))
    const controles = [this.seleccionable, this.searchInput].filter(Boolean)
    if (!option) {
      controles.forEach(control => control.removeAttribute('aria-activedescendant'))
      this.activeOption = null
      return
    }
    controles.forEach(control => control.setAttribute('aria-activedescendant', option.id))
    this.activeOption = option
    option.scrollIntoView?.({ block: 'nearest' })
  }

  moveActiveOption (paso) {
    const opciones = this.navigableOptions
    if (!opciones.length) return

    const actual = opciones.indexOf(this.activeOption)
    const destino = Math.min(Math.max(actual + paso, 0), opciones.length - 1)
    this.setActiveOption(opciones[actual === -1 ? 0 : destino])
  }

  chooseOption (option) {
    if (!option || option.hasAttribute('aria-disabled')) return

    const index = Number(option.dataset.index)
    if (index !== this.selectedIndex) {
      this.selectedIndex = index
      this.syncFromNative()
      this.dispatchEvent(new Event('input', { bubbles: true }))
      this.dispatchEvent(new Event('change', { bubbles: true }))
    }
    this.closeSelect()
    this.seleccionable.focus()
  }

  onKeydown (event) {
    if (this.disabled) return
    if (!this.isOpen) {
      this.onKeydownClosed(event)
      return
    }

    switch (event.key) {
    case 'ArrowDown':
      event.preventDefault()
      event.altKey ? this.chooseOption(this.activeOption) : this.moveActiveOption(1)
      break
    case 'ArrowUp':
      event.preventDefault()
      event.altKey ? this.chooseOption(this.activeOption) : this.moveActiveOption(-1)
      break
    case 'PageDown':
      event.preventDefault()
      this.moveActiveOption(SALTO_DE_PAGINA)
      break
    case 'PageUp':
      event.preventDefault()
      this.moveActiveOption(-SALTO_DE_PAGINA)
      break
    case 'Home':
      if (event.target === this.searchInput) return
      event.preventDefault()
      this.setActiveOption(this.navigableOptions[0])
      break
    case 'End':
      if (event.target === this.searchInput) return
      event.preventDefault()
      this.setActiveOption(this.navigableOptions.at(-1))
      break
    case 'Enter':
      event.preventDefault()
      this.chooseOption(this.activeOption)
      break
    case ' ':
      if (event.target === this.searchInput) return
      event.preventDefault()
      this.chooseOption(this.activeOption)
      break
    case 'Escape':
      event.preventDefault()
      this.closeSelect()
      break
    case 'Tab':
      this.chooseOption(this.activeOption)
      break
    default:
      if (event.target !== this.searchInput) this.typeAhead(event)
    }
  }

  onKeydownClosed (event) {
    if (event.key === 'Tab' || event.key === 'Escape') return

    if (TECLAS_QUE_ABREN.includes(event.key)) {
      event.preventDefault()
      this.openSelect()
      if (event.key === 'Home') this.setActiveOption(this.navigableOptions[0])
      if (event.key === 'End') this.setActiveOption(this.navigableOptions.at(-1))
      return
    }
    if (this.typeAhead(event)) this.openSelect()
  }

  // Escribir salta a la primera opción que empieza por lo tecleado (las letras
  // seguidas se acumulan un momento, como en un select nativo). Devuelve si la
  // tecla era imprimible.
  typeAhead (event) {
    if (event.key.length !== 1 || event.ctrlKey || event.metaKey || event.altKey) return false

    clearTimeout(this.typeAheadTimeout)
    this.typeAheadBuffer = (this.typeAheadBuffer || '') + this.normalizeForSearch(event.key)
    this.typeAheadTimeout = setTimeout(() => { this.typeAheadBuffer = '' }, PAUSA_BUSQUEDA_AL_TECLEAR)

    const coincidencia = this.navigableOptions.find(option =>
      this.normalizeForSearch(option.textContent).startsWith(this.typeAheadBuffer))
    if (!coincidencia) return true

    if (this.isOpen) {
      this.setActiveOption(coincidencia)
    } else {
      this.openSelect()
      this.setActiveOption(coincidencia)
    }
    return true
  }
}

export default SelectCustom
